import { useEffect, useState } from 'react'
import useUserRole from 'src/hooks/useUserRole'
import { useVenueCustomRoles } from 'src/hooks/useIsBlocked'
import { canAccess } from 'src/utils/permissions'
import {
  fetchFoodSafetyHome,
  forgetFoodSafetyHomeInFlight,
} from 'src/utils/foodSafetyApi'
import {
  foodSafetyTodayBadgeCount,
  withoutFoodSafetyStillNeeded,
} from 'src/utils/foodSafetyHome'
import { notifyVenueHomeChanged } from 'src/utils/venueHomeApi'

/** Nav badge poll; Home refreshes on the same beat while showing today. */
export const FOOD_SAFETY_POLL_INTERVAL_MS = 20_000

/**
 * Fired after Food Safety logs / skips / setup saves so the nav badge
 * refreshes immediately instead of waiting for the next poll.
 */
export const FOOD_SAFETY_CHANGED_EVENT = 'oms:food-safety-changed'

/**
 * Fired with `{ venueId, home }` after each badge load of today's Home, so
 * a Home screen showing today reuses it instead of polling on its own.
 */
export const FOOD_SAFETY_HOME_POLLED_EVENT = 'oms:food-safety-home-polled'

export const notifyFoodSafetyChanged = () => {
  forgetFoodSafetyHomeInFlight()
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(FOOD_SAFETY_CHANGED_EVENT))
  notifyVenueHomeChanged()
}

/**
 * Today’s open-section count for the Food Safety top-nav orange badge.
 * Same fail-silent / poll / focus pattern as Approvals.
 */
const useFoodSafetyTodayBadgeCount = venueId => {
  const [count, setCount] = useState(0)
  const role = useUserRole()
  const customRoles = useVenueCustomRoles()
  const canSeeFoodSafety = canAccess('food-safety', role, customRoles)

  useEffect(() => {
    if (!venueId || !canSeeFoodSafety) {
      setCount(0)
      return undefined
    }

    let cancelled = false

    const load = () => {
      fetchFoodSafetyHome(venueId)
        .then(home => {
          if (cancelled) return null
          setCount(
            foodSafetyTodayBadgeCount(withoutFoodSafetyStillNeeded(home)),
          )
          window.dispatchEvent(
            new CustomEvent(FOOD_SAFETY_HOME_POLLED_EVENT, {
              detail: { venueId, home },
            }),
          )
          return null
        })
        .catch(error => {
          console.error('useFoodSafetyTodayBadgeCount poll failed', error)
          return null
        })
    }

    load()
    const interval = setInterval(load, FOOD_SAFETY_POLL_INTERVAL_MS)

    const onFocus = () => load()
    window.addEventListener('focus', onFocus)
    window.addEventListener(FOOD_SAFETY_CHANGED_EVENT, onFocus)

    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener(FOOD_SAFETY_CHANGED_EVENT, onFocus)
    }
  }, [venueId, canSeeFoodSafety])

  return count
}

export default useFoodSafetyTodayBadgeCount
